import React, { useEffect, useState } from 'react';
import api from '../utils/api';

const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const typeLabels = { reclamation: '🔴 Réclamation', suggestion: '💡 Suggestion', satisfaction: '⭐ Satisfaction' };
const statutLabels = { en_attente: '⏳ En attente', en_cours: '🔄 En cours', traite: '✅ Traité', ferme: '🔒 Fermé' };
const statutColors = {
    en_attente: { bg: 'rgba(217,119,6,0.15)', color: '#fbbf24', border: 'rgba(217,119,6,0.3)' },
    en_cours: { bg: 'rgba(2,132,199,0.15)', color: '#38bdf8', border: 'rgba(2,132,199,0.3)' },
    traite: { bg: 'rgba(5,150,105,0.15)', color: '#34d399', border: 'rgba(5,150,105,0.3)' },
    ferme: { bg: 'rgba(100,116,139,0.15)', color: '#94a3b8', border: 'rgba(100,116,139,0.3)' },
};
const typeColors = {
    reclamation: { bg: 'rgba(239,68,68,0.15)', color: '#f87171', border: 'rgba(239,68,68,0.3)' },
    suggestion: { bg: 'rgba(0,212,255,0.15)', color: '#00d4ff', border: 'rgba(0,212,255,0.3)' },
    satisfaction: { bg: 'rgba(16,185,129,0.15)', color: '#34d399', border: 'rgba(16,185,129,0.3)' },
};

const StatCard = ({ icon, value, label, color, glow }) => (
    <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)', border: `1px solid ${color}30`, borderRadius: 16, padding: 24, position: 'relative', overflow: 'hidden', boxShadow: `0 0 30px ${glow}15` }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${color},transparent)` }}></div>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}20`, border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 16 }}>{icon}</div>
        <div style={{ fontSize: 34, fontWeight: 800, color: 'white', lineHeight: 1, marginBottom: 4 }}>{value}</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>{label}</div>
    </div>
);

const DashboardPage = () => {
    const [stats, setStats] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const [s, m] = await Promise.all([
                    api.get('/messages/stats'),
                    api.get('/messages?limit=5&page=1'),
                ]);
                setStats(s.data.stats);
                setMessages(m.data.messages);
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        load();
    }, []);

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 16 }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(0,212,255,0.2)', borderTopColor: '#00d4ff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>Chargement...</p>
        </div>
    );

    if (!stats) return null;
    const total = stats.total || 1;

    return (
        <div>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, marginBottom: 28 }}>
                <StatCard icon="📨" value={stats.total} label="Total messages" color="#00d4ff" glow="#00d4ff" />
                <StatCard icon="⏳" value={stats.en_attente} label="En attente" color="#fbbf24" glow="#fbbf24" />
                <StatCard icon="🔄" value={stats.en_cours} label="En cours" color="#38bdf8" glow="#38bdf8" />
                <StatCard icon="✅" value={stats.traite} label="Traités" color="#34d399" glow="#34d399" />
            </div>

            {/* Graphiques */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 16, padding: 24 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'white', marginBottom: 20 }}>📊 Répartition par type</div>
                    {[
                        { label: 'Réclamations', value: stats.reclamations, color: '#f87171' },
                        { label: 'Suggestions', value: stats.suggestions, color: '#00d4ff' },
                        { label: 'Satisfactions', value: stats.satisfactions, color: '#34d399' },
                    ].map(({ label, value, color }) => (
                        <div key={label} style={{ marginBottom: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                                <span style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</span>
                                <strong style={{ color }}>{value}</strong>
                            </div>
                            <div style={{ height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${Math.round(value / total * 100)}%`, background: `linear-gradient(90deg,${color},${color}80)`, borderRadius: 3, boxShadow: `0 0 8px ${color}50` }}></div>
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 16, padding: 24 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'white', marginBottom: 20 }}>📈 Statuts des dossiers</div>
                    {[
                        { label: 'En attente', value: stats.en_attente, color: '#fbbf24' },
                        { label: 'En cours', value: stats.en_cours, color: '#38bdf8' },
                        { label: 'Traités', value: stats.traite, color: '#34d399' },
                    ].map(({ label, value, color }) => (
                        <div key={label} style={{ marginBottom: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                                <span style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</span>
                                <strong style={{ color }}>{value}</strong>
                            </div>
                            <div style={{ height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${Math.round(value / total * 100)}%`, background: `linear-gradient(90deg,${color},${color}80)`, borderRadius: 3, boxShadow: `0 0 8px ${color}50` }}></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Derniers messages */}
            <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 16, overflow: 'hidden' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(0,212,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'white' }}>🕐 Derniers messages</div>
                        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>5 messages récents</div>
                    </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                {['Référence', 'Client', 'Type', 'Objet', 'Statut', 'Date'].map(h => (
                                    <th key={h} style={{ textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.6)', borderBottom: '1px solid rgba(0,212,255,0.1)', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {messages.map(m => {
                                const tc = typeColors[m.type_message] || typeColors.suggestion;
                                const sc = statutColors[m.statut] || statutColors.ferme;
                                return (
                                    <tr key={m.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: '#00d4ff', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)', padding: '3px 8px', borderRadius: 6 }}>{m.reference}</span>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <div style={{ fontWeight: 600, color: 'white', fontSize: 13 }}>{m.prenom} {m.nom}</div>
                                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{m.email}</div>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: tc.bg, color: tc.color, border: `1px solid ${tc.border}` }}>{typeLabels[m.type_message]}</span>
                                        </td>
                                        <td style={{ padding: '14px 16px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{m.objet}</td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{statutLabels[m.statut]}</span>
                                        </td>
                                        <td style={{ padding: '14px 16px', fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{formatDate(m.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;